import React, { useEffect } from "react";
import { Drawer } from "@mui/material";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import {
  MOBILE_DRAWER_BREAKPOINT,
  useSidebarDrawer,
} from "../../context/SidebarDrawerContext";
import "./MobileSidebarDrawer.css";

/**
 * Right-hand drawer that hosts the dashboard navigation on mobile.
 *
 * It is a shell only — each dashboard renders it with its own nav as children
 * (Sidebar for Teacher/Admin/SuperAdmin, the tab list for Student), so those
 * navs keep their existing state and props. The header just triggers open/close
 * through SidebarDrawerContext.
 *
 * `breakpoint` is the width below which the drawer replaces the inline nav;
 * MobileSidebarDrawer.css hides the inline nav at the same widths.
 */
const MobileSidebarDrawer = ({
  children,
  breakpoint = MOBILE_DRAWER_BREAKPOINT,
}) => {
  const { isDrawerOpen, closeDrawer, registerDrawer } = useSidebarDrawer();
  const location = useLocation();
  const globalState = useSelector((state) => state.globalState);

  const role = globalState?.role;
  const teacherRole =
    globalState?.teacher_details?.data?.teacher_department_roles || role;
  const profileName =
    role === "Student"
      ? (globalState?.student_details?.data?.name ?? role)
      : role === "Teacher"
        ? (globalState?.teacher_details?.data?.name ?? role)
        : role;

  // Tell the header a drawer exists on this route (and below which width), so
  // it renders the hamburger.
  useEffect(() => {
    registerDrawer(breakpoint);
    return () => registerDrawer(0);
  }, [registerDrawer, breakpoint]);

  // Any navigation made from inside the drawer dismisses it.
  useEffect(() => {
    closeDrawer();
  }, [location.pathname, closeDrawer]);

  const handleContentClick = (event) => {
    const trigger = event.target.closest("a, .create-course");
    if (!trigger) return;
    // Tapping the same route again doesn't fire the effect above, and the popup
    // triggers ("Create Course"/"Add School"/"Add Admin") render outside the
    // drawer — both still need it dismissed. Links that only expand a
    // collapsible section carry the arrow, and keep it open so the sub-items
    // stay reachable.
    if (trigger.querySelector && trigger.querySelector(".arrow")) return;
    closeDrawer();
  };

  return (
    <Drawer
      anchor="right"
      open={isDrawerOpen}
      onClose={closeDrawer}
      className="sidebar-drawer"
      PaperProps={{ className: "sidebar-drawer-paper" }}
    >
      <div className="sidebar-drawer-header">
        <div className="sidebar-drawer-user">
          <p className="sidebar-drawer-user-name">
            {teacherRole !== "Admin" ? profileName : "Admin"}
          </p>
          <p className="sidebar-drawer-user-role">
            {teacherRole === "hod" ? "Head Of Department" : teacherRole}
          </p>
        </div>
        <button
          type="button"
          className="sidebar-drawer-close"
          aria-label="Close menu"
          onClick={closeDrawer}
        >
          &times;
        </button>
      </div>

      <div className="sidebar-drawer-content" onClick={handleContentClick}>
        {children}
      </div>
    </Drawer>
  );
};

export default MobileSidebarDrawer;
